import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import ConfirmDialog from '../../../common/components/confirm-dialog';
import { IconEdit, IconFarm, IconPlus, IconTrash } from '../../../common/icons';
import { useClientFarms } from '../../../common/contexts/farms';
import { farmKeys } from '../../../common/queries/farms';
import { envelopeData } from '../../../core/network/envelope';
import { useDeleteFarm } from '../contexts/farm-mutations';
import FarmForm from '../forms/farm-form';

import '../styles/overview.css';

// TSS vite/03 — "Mis fincas": CRUD de las fincas del cliente (public-farms.md).
// Lectura COMPUESTA → envelope: items/pagination con envelopeData(). Escrituras
// (form/borrado) leen r.data plano y refrescan la lista por invalidación de
// farmKeys (hooks en contexts/farm-mutations.jsx). Paginación offset (patrón
// parcerías/vacas; contract: limit def 50 / máx 500).
//
// El borrado con dependencias responde 409 { fields:{recipients,submissions} }:
// no es un toast genérico — el ConfirmDialog muestra los conteos que bloquean.

const PAGE_SIZE = 50;

function sizeLabel(size) {
    if (size == null) return null;
    return `${size} ha`;
}

function FarmCard({ farm, onEdit, onDelete }) {
    return (
        <article className="finca-card">
            <div className="finca-card__main">
                <span className="finca-card__name">{farm.name}</span>
                <div className="finca-card__meta">
                    {farm.responsible && (
                        <span className="finca-card__responsible">{farm.responsible}</span>
                    )}
                    {sizeLabel(farm.size) && (
                        <span className="finca-card__size">{sizeLabel(farm.size)}</span>
                    )}
                </div>
            </div>
            <div className="finca-card__actions">
                <button
                    type="button"
                    className="finca-card__action"
                    onClick={() => onEdit(farm)}
                    aria-label={`Editar ${farm.name}`}
                >
                    <IconEdit />
                </button>
                <button
                    type="button"
                    className="finca-card__action finca-card__action--danger"
                    onClick={() => onDelete(farm)}
                    aria-label={`Eliminar ${farm.name}`}
                >
                    <IconTrash />
                </button>
            </div>
        </article>
    );
}

export default function FincasOverview() {
    const [params, setParams] = useSearchParams();
    const qc = useQueryClient();

    const offset = Math.max(0, Number(params.get('offset')) || 0);

    const { data, isLoading, isFetching, isError, error } =
        useClientFarms({ limit: PAGE_SIZE, offset });

    const payload = envelopeData(data);
    const items = payload.items ?? [];
    const pagination = payload.pagination ?? {};
    const total = pagination.total ?? null;

    const hasPrev = offset > 0;
    const hasNext = total != null ? offset + items.length < total : items.length === PAGE_SIZE;

    // UI state (modales) — local, no va a la URL.
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // finca en edición | null
    const [deleting, setDeleting] = useState(null);  // finca a borrar | null
    const [blockMsg, setBlockMsg] = useState('');    // aviso enriquecido del 409

    const del = useDeleteFarm();

    const goTo = (nextOffset) => {
        const next = new URLSearchParams(params);
        if (nextOffset > 0) next.set('offset', String(nextOffset));
        else next.delete('offset');
        setParams(next, { replace: true });
    };

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (farm) => { setEditing(farm); setFormOpen(true); };
    const closeForm = () => { setFormOpen(false); setEditing(null); };

    const askDelete = (farm) => { setBlockMsg(''); setDeleting(farm); };
    const closeDelete = () => { setDeleting(null); setBlockMsg(''); };

    const confirmDelete = () => {
        if (!deleting) return;
        setBlockMsg('');
        del.mutate(deleting.id, {
            onSuccess: () => {
                toast.success('Finca eliminada');
                setDeleting(null);
            },
            onError: (err) => {
                if (err?.status === 409) {
                    // Guard referencial: no borra fincas con dependencias activas.
                    const f = err.fields || {};
                    const parts = [];
                    if (f.recipients) parts.push(`${f.recipients} receptora${f.recipients === 1 ? '' : 's'}`);
                    if (f.submissions) parts.push(`${f.submissions} propuesta${f.submissions === 1 ? '' : 's'} de donadora`);
                    setBlockMsg(
                        parts.length
                            ? `No puedes eliminar esta finca porque tiene registros vinculados: ${parts.join(' y ')}. Reubícalos o elimínalos primero.`
                            : (err.message || 'No puedes eliminar esta finca: tiene registros vinculados.'),
                    );
                } else if (err?.status === 404) {
                    toast.error('Finca no encontrada');
                    qc.invalidateQueries({ queryKey: farmKeys.all });
                    setDeleting(null);
                } else if (err?.status !== 403) {
                    toast.error(err?.message || 'No se pudo eliminar la finca');
                }
            },
        });
    };

    if (isLoading) {
        return <div className="fincas__loading">Cargando tus fincas...</div>;
    }

    if (isError) {
        return (
            <div className="fincas__error">
                {error?.message || 'No se pudieron cargar tus fincas'}
            </div>
        );
    }

    return (
        <section className="fincas">
            <header className="fincas__header">
                <div className="fincas__heading">
                    <h2 className="fincas__title">Mis fincas</h2>
                    {total != null && (
                        <span className="fincas__count">
                            {total} finca{total === 1 ? '' : 's'}
                        </span>
                    )}
                </div>
                <button type="button" className="fincas__new" onClick={openCreate}>
                    <IconPlus />
                    <span>Nueva finca</span>
                </button>
            </header>

            {!items.length ? (
                <div className="fincas__empty">
                    <span className="fincas__empty-icon"><IconFarm /></span>
                    <p className="fincas__empty-text">Aún no tienes fincas registradas.</p>
                    <button type="button" className="fincas__empty-cta" onClick={openCreate}>
                        <IconPlus />
                        <span>Crea tu primera finca</span>
                    </button>
                </div>
            ) : (
                <div className={`fincas__list ${isFetching ? 'fincas__list--refreshing' : ''}`}>
                    {items.map((farm) => (
                        <FarmCard key={farm.id} farm={farm} onEdit={openEdit} onDelete={askDelete} />
                    ))}
                </div>
            )}

            {(hasPrev || hasNext) && (
                <nav className="fincas__pager">
                    <button
                        className="fincas__pager-btn"
                        onClick={() => goTo(offset - PAGE_SIZE)}
                        disabled={!hasPrev || isFetching}
                    >
                        ← Anteriores
                    </button>
                    <span className="fincas__pager-info">
                        {offset + 1}–{offset + items.length}
                    </span>
                    <button
                        className="fincas__pager-btn"
                        onClick={() => goTo(offset + PAGE_SIZE)}
                        disabled={!hasNext || isFetching}
                    >
                        Siguientes →
                    </button>
                </nav>
            )}

            {formOpen && (
                <FarmForm farm={editing} onClose={closeForm} onSaved={closeForm} />
            )}

            <ConfirmDialog
                open={!!deleting}
                title="Eliminar finca"
                message={
                    deleting
                        ? `¿Seguro que quieres eliminar "${deleting.name}"? Esta acción no se puede deshacer.`
                        : ''
                }
                confirmLabel="Eliminar"
                danger
                busy={del.isPending}
                onConfirm={confirmDelete}
                onClose={closeDelete}
            >
                {blockMsg && <p className="fincas__block">{blockMsg}</p>}
            </ConfirmDialog>
        </section>
    );
}
